// Post-registration investor pitch deck — shown to applicants who registered
// as investors but have NOT yet been approved for live demo access.
//
// Order per founder spec: Slide 13 (Big Picture) → Slide 10 (Pilot) →
// Slide 11 (Market Size) → Slide 12 (5-Year Plan). All "seafood" copy
// scrubbed in line with global brand rules (premium food / aquaculture /
// India → USA Premium Food Corridor).
import { motion } from "framer-motion";
import {
  Globe2, ShieldCheck, Target, Layers, TrendingUp, Sparkles, Cpu,
  Network, Database, Banknote, Compass, Award, Zap,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

/* ============================ SLIDE 13 — BIG PICTURE ============================ */
function SlideBigPicture() {
  const pillars = [
    { icon: Compass,    title: "Real Problem",        desc: "High friction, long cycles, trust gaps." },
    { icon: Sparkles,   title: "Simple Solution",     desc: "Operate, enable, move, get paid." },
    { icon: Network,    title: "Scalable Model",      desc: "Asset-light platform with recurring revenue." },
    { icon: Globe2,     title: "Massive Opportunity", desc: "$8T+ TAM of global food trade." },
  ];
  const impact = [
    { title: "WE CONNECT", desc: "global producers to global markets" },
    { title: "WE SIMPLIFY", desc: "complex trade end to end" },
    { title: "WE BUILD TRUST", desc: "with transparency, quality & compliance" },
    { title: "WE CREATE VALUE", desc: "through efficiency, scale & intelligence" },
    { title: "WE DRIVE IMPACT", desc: "for businesses, communities & planet" },
  ];

  return (
    <section className="border-y border-[#1A2E22] bg-[#070F0A] py-20 sm:py-24" data-testid="pitch-slide-big-picture">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div {...fade(0)} className="mb-12">
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold mb-3">The Big Picture</p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
            We are building the operating <br className="hidden sm:block" />
            <span className="text-[#22C55E]">infrastructure of global food trade.</span>
          </h2>
          <p className="text-[#A1B2A8] text-base sm:text-lg mt-5 max-w-3xl">
            Connecting producers to markets. Simplifying trade. Creating shared prosperity.
          </p>
        </motion.div>

        {/* 4 pillars */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
          {pillars.map((p, i) => (
            <motion.div key={p.title} {...fade(0.05 + i * 0.06)} className="border border-[#1A2E22] bg-[#09120D] p-5">
              <p.icon className="w-5 h-5 text-[#22C55E] mb-3" />
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#22C55E] mb-1">{p.title}</p>
              <p className="text-white text-sm font-semibold leading-snug">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Flow diagram */}
        <motion.div {...fade(0.2)} className="border border-[#1A2E22] bg-[#09120D] p-6 sm:p-8 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-right">
              <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.18em] font-semibold mb-1">Producers</p>
              <p className="text-white text-base font-semibold">Exporters</p>
            </div>
            <div className="border border-[#22C55E]/40 bg-[#22C55E]/5 px-5 py-4 text-center">
              <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">Delta GreenX</p>
              <p className="text-white text-base font-bold mb-2">Trade Operating Platform</p>
              <p className="text-[#A1B2A8] text-[11px] uppercase tracking-wider">
                Trust · Traceability · Intelligence · Execution
              </p>
            </div>
            <div className="text-center md:text-left">
              <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.18em] font-semibold mb-1">Buyers</p>
              <p className="text-white text-base font-semibold">Importers &amp; Retailers</p>
              <p className="text-[#A1B2A8]/60 text-[11px] mt-1">→ Consumers Worldwide</p>
            </div>
          </div>
        </motion.div>

        {/* Impact strip */}
        <motion.p {...fade(0.25)} className="font-['Outfit'] text-center text-[#22C55E] text-xs uppercase tracking-[0.3em] font-bold mb-6">
          One infrastructure. Global impact.
        </motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {impact.map((b, i) => (
            <motion.div key={b.title} {...fade(0.3 + i * 0.05)} className="border border-[#1A2E22] bg-[#09120D] p-4 text-center">
              <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.18em] font-bold">{b.title}</p>
              <p className="text-white text-xs mt-1.5">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.p {...fade(0.5)} className="text-[#A1B2A8] text-center text-sm sm:text-base mt-12 max-w-3xl mx-auto leading-relaxed italic">
          Together, we can build a food system that is connected, efficient and sustainable for generations to come.
        </motion.p>
      </div>
    </section>
  );
}

/* ============================ SLIDE 10 — PILOT ============================ */
function SlidePilot() {
  const targets = [
    { label: "Target Participants", value: "Exporters Only", sub: "Premium food / aquaculture exporters in India" },
    { label: "Corridor",            value: "India → USA",  sub: "Premium Food Corridor" },
    { label: "Duration",            value: "6 Months",     sub: "Phase 1 proof of execution" },
    { label: "Focus",               value: "Commercial Intelligence", sub: "Public-registry signals + KYC documents" },
    { label: "Objective",           value: "Reduce cycles", sub: "Improve working capital" },
  ];
  const steps = [
    { n: "01", title: "Onboard exporters", desc: "Identify and onboard quality premium-food exporters" },
    { n: "02", title: "Collect KYC & docs", desc: "Build verified commercial profiles" },
    { n: "03", title: "Deliver intelligence", desc: "Risk · credit · trade history · pricing · market & performance insights" },
    { n: "04", title: "Drive value & measure", desc: "Reduce cycles · improve realizations · unlock working capital" },
  ];
  const economics = [
    { label: "Target Exporters",            value: "20" },
    { label: "Deployed Capital (Working)",  value: "$1M" },
    { label: "Supported Trade Flow (6 mo)", value: "$4M – $5M", sub: "~4 capital turns" },
    { label: "Avg. Cash Flow Cycle",        value: "45–50 d" },
    { label: "Execution & Funding Fee",     value: "~$70K – $88K", sub: "avg. 1.75%" },
    { label: "Priority Membership",         value: "~$60K", sub: "$500/mo / exporter" },
  ];
  const impact = [
    { title: "Cash Flow Cycle Reduction", value: "45–50 days", sub: "Faster realizations & improved working capital" },
    { title: "Working Capital Improvement", value: "25–35%", sub: "Better capital efficiency, lower financing cost" },
    { title: "Realization Improvement", value: "1–2%", sub: "Better pricing through data-led insights" },
    { title: "Risk Reduction & Confidence", value: "Higher", sub: "Stronger counterparty confidence & decisions" },
  ];

  return (
    <section className="bg-[#050A07] py-20 sm:py-24" data-testid="pitch-slide-pilot">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div {...fade(0)} className="mb-10">
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold mb-3">Pilot Go-to-Market</p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
            India → USA Premium Food Corridor
          </h2>
          <p className="text-[#A1B2A8] text-base sm:text-lg mt-4 max-w-3xl">
            Start focused. Prove value with exporters. Build the foundation to scale.
          </p>
        </motion.div>

        {/* Pilot focus */}
        <motion.div {...fade(0.05)} className="mb-10">
          <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">1 · Pilot Focus</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {targets.map((t, i) => (
              <div key={t.label} className="border border-[#1A2E22] bg-[#09120D] p-4" data-testid={`pilot-target-${i}`}>
                <p className="text-[#22C55E] text-[10px] uppercase tracking-wider font-semibold">{t.label}</p>
                <p className="text-white font-['Outfit'] font-bold text-base mt-2">{t.value}</p>
                <p className="text-[#A1B2A8]/60 text-[10px] mt-1 leading-snug">{t.sub}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* GTM steps */}
        <motion.div {...fade(0.1)} className="mb-10">
          <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">2 · Go-to-Market Approach</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {steps.map((s) => (
              <div key={s.n} className="border border-[#1A2E22] bg-[#09120D] p-5">
                <p className="font-['Outfit'] text-2xl font-bold text-[#22C55E]">{s.n}</p>
                <p className="text-white text-sm font-semibold mt-2">{s.title}</p>
                <p className="text-[#A1B2A8]/70 text-xs mt-1 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pilot economics */}
        <motion.div {...fade(0.15)} className="mb-10">
          <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">3 · Pilot Economics (6 Months)</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {economics.map((e, i) => (
              <div key={e.label} className="border border-[#1A2E22] bg-[#09120D] p-5" data-testid={`pilot-econ-${i}`}>
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider font-semibold">{e.label}</p>
                <p className="text-white font-['Outfit'] font-bold text-2xl mt-2">{e.value}</p>
                {e.sub && <p className="text-[#A1B2A8]/50 text-[10px] mt-1">{e.sub}</p>}
              </div>
            ))}
          </div>
          <div className="mt-3 border border-[#22C55E]/40 bg-[#22C55E]/5 p-5 flex items-center justify-between">
            <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold">Estimated Pilot Revenue (6 mo)</p>
            <p className="text-[#22C55E] font-['Outfit'] font-bold text-2xl">~$130K – $150K</p>
          </div>
        </motion.div>

        {/* Impact */}
        <motion.div {...fade(0.2)} className="mb-10">
          <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">4 · Expected Impact (6 Months)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {impact.map((m) => (
              <div key={m.title} className="border border-[#1A2E22] bg-[#09120D] p-5">
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider font-semibold">{m.title}</p>
                <p className="text-white font-['Outfit'] font-bold text-xl mt-2">{m.value}</p>
                <p className="text-[#A1B2A8]/60 text-[11px] mt-1 leading-relaxed">{m.sub}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Investment & Use of Funds */}
        <motion.div {...fade(0.25)} className="border border-[#22C55E]/40 bg-gradient-to-br from-[#0d1f14] to-[#070F0A] p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Banknote className="w-4 h-4 text-[#22C55E]" />
            <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold">5 · Investment &amp; Use of Funds</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-left">
              <p className="font-['Outfit'] text-5xl font-bold text-[#22C55E]">$2M</p>
              <p className="text-white text-sm font-semibold mt-1">Total Raise</p>
            </div>
            <div className="border border-[#1A2E22] bg-[#09120D] p-4">
              <p className="text-[#22C55E] text-[10px] uppercase tracking-wider font-semibold mb-2">$1M · Platform Development</p>
              <ul className="text-[#A1B2A8] text-xs space-y-1 leading-relaxed">
                <li>· Product development</li>
                <li>· Engineering &amp; data integration</li>
                <li>· Compliance &amp; security</li>
                <li>· Operations &amp; team</li>
              </ul>
            </div>
            <div className="border border-[#1A2E22] bg-[#09120D] p-4">
              <p className="text-[#22C55E] text-[10px] uppercase tracking-wider font-semibold mb-2">$1M · Pilot Execution Capital</p>
              <ul className="text-[#A1B2A8] text-xs space-y-1 leading-relaxed">
                <li>· Working capital for exporter transactions</li>
                <li>· Operations &amp; onboarding</li>
                <li>· Customer support &amp; compliance</li>
              </ul>
            </div>
          </div>
        </motion.div>

        <p className="text-[#A1B2A8]/40 text-[10px] mt-6 italic">Note: Numbers are estimates for pilot period and may vary.</p>
      </div>
    </section>
  );
}

/* ============================ SLIDE 11 — MARKET SIZE ============================ */
function SlideMarketSize() {
  const layers = [
    {
      label: "TAM",
      title: "$8T+",
      sub: "Global Food Commerce Ecosystem",
      desc: "Production · Processing · Logistics · Wholesale · Retail · Food Service · Trade & Financing — across the full value chain and all geographies.",
      accent: "text-[#22C55E] border-[#22C55E]/40",
    },
    {
      label: "SAM",
      title: "$300B – $500B",
      sub: "Cross-border food trade corridors",
      desc: "Corridors with working capital and trust gaps — premium food / aquaculture, rice, spices, processed foods, other commodities.",
      accent: "text-cyan-400 border-cyan-400/30",
    },
    {
      label: "SOM (3 Years)",
      title: "$1B – $3B",
      sub: "Initial corridors → scalable global opportunity",
      desc: "Phase 1 (0–12m) India → USA Premium Food · Phase 2 (12–30m) EU · Canada · UK · Middle East · SEA · Phase 3 (30m+) rice · spices · pulses · fruits & vegetables · dairy · meat · others.",
      accent: "text-purple-400 border-purple-400/30",
    },
  ];
  const phases = [
    { phase: "Phase 1 (0–12m)",  title: "Proof of Execution",   desc: "India → USA Premium Food Corridor · 20 exporters · build trust & data network · optimize working capital cycles · prove commercial model" },
    { phase: "Phase 2 (12–30m)", title: "Expand Globally",      desc: "Expand premium food to more countries · add importers & downstream buyers · scale trade volume & working capital · strengthen risk intelligence" },
    { phase: "Phase 3 (30+m)",   title: "Diversify Commodities",desc: "Add new commodities to platform · expand across major food corridors · deepen ecosystem & financial products · become global food trade infrastructure" },
  ];

  return (
    <section className="border-y border-[#1A2E22] bg-[#070F0A] py-20 sm:py-24" data-testid="pitch-slide-market">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div {...fade(0)} className="mb-12">
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold mb-3">Market Size · Opportunity</p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
            Building the global food trade infrastructure
          </h2>
          <p className="text-[#A1B2A8] text-base sm:text-lg mt-4 max-w-3xl">
            Premium food &amp; aquaculture is our proof of execution. The platform is built for all food commodities, everywhere.
          </p>
        </motion.div>

        {/* TAM/SAM/SOM tiers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-12">
          {layers.map((l, i) => (
            <motion.div key={l.label} {...fade(0.05 + i * 0.07)} className={`border ${l.accent} bg-[#09120D] p-6`} data-testid={`market-tier-${l.label.toLowerCase().split(' ')[0]}`}>
              <p className={`text-[10px] uppercase tracking-[0.2em] font-bold ${l.accent.split(' ')[0]}`}>{l.label}</p>
              <p className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mt-2">{l.title}</p>
              <p className="text-white/80 text-sm font-semibold mt-1">{l.sub}</p>
              <p className="text-[#A1B2A8]/70 text-xs mt-3 leading-relaxed">{l.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Pilot proof strip */}
        <motion.div {...fade(0.25)} className="border border-[#1A2E22] bg-[#09120D] p-6 mb-12">
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">Pilot Proof of Execution · India → USA Premium Food Corridor</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { l: "Exporters",        v: "20" },
              { l: "Capital Deployed", v: "$1M" },
              { l: "Trade Flow",       v: "$4M-$5M", s: "~4 capital turns" },
              { l: "Cycle",            v: "45–50 d" },
              { l: "Pilot Revenue",    v: "$130K-$150K", s: "6 months" },
            ].map((s, i) => (
              <div key={i} className="border border-[#1A2E22] bg-[#050A07] p-3">
                <p className="text-[#A1B2A8] text-[9px] uppercase tracking-wider">{s.l}</p>
                <p className="text-white font-['Outfit'] font-bold text-base mt-1">{s.v}</p>
                {s.s && <p className="text-[#A1B2A8]/50 text-[9px] mt-0.5">{s.s}</p>}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Phased roadmap */}
        <motion.div {...fade(0.3)} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {phases.map((p, i) => (
            <div key={p.phase} className="border border-[#1A2E22] bg-[#09120D] p-5 relative" data-testid={`market-phase-${i + 1}`}>
              <span className="absolute -top-2 left-5 bg-[#22C55E] text-[#050A07] text-[9px] font-bold uppercase tracking-wider px-2 py-0.5">{p.phase}</span>
              <p className="text-white font-['Outfit'] font-bold text-base mt-2">{p.title}</p>
              <p className="text-[#A1B2A8]/70 text-xs mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* North Star */}
        <motion.div {...fade(0.45)} className="mt-10 border border-[#22C55E]/40 bg-[#22C55E]/5 p-6 sm:p-8 text-center">
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-bold mb-2">Our North Star</p>
          <p className="font-['Outfit'] text-xl sm:text-2xl text-white max-w-3xl mx-auto leading-snug">
            To become the most trusted, efficient and capital-smart <br className="hidden sm:block" />
            infrastructure for global food trade.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================ SLIDE 12 — 5-YEAR PLAN ============================ */
function SlideFiveYearPlan() {
  const years = [
    {
      year: "Year 1",
      title: "Proof of Execution",
      focus: "India → USA Premium Food Corridor",
      bullets: ["20–30 exporters", "$1M building investment", "$1M working capital deployed", "$4M–$5M trade flow", "45–50 day cycle"],
      revenue: "~$130K – $150K",
      milestone: "Prove unit economics and cycle compression",
      scale: "$15M – $20M",
    },
    {
      year: "Year 2",
      title: "Premium Food Scale-Up + Importer & Buyer Support",
      focus: "Expand corridors · Canada · UK · UAE · Singapore",
      bullets: ["75–100 exporters", "20–40 importers", "Importer & buyer support (working capital + inventory)"],
      revenue: "~$1M – $2M",
      milestone: "Scale corridor & start supporting importers and buyers",
      scale: "$40M – $60M",
    },
    {
      year: "Year 3",
      title: "Global Corridors + Importer & Buyer Support at Scale",
      focus: "Add new sourcing corridors · Vietnam · Thailand · Indonesia · more",
      bullets: ["200+ ecosystem participants", "$75M – $100M trade flow", "Importer & buyer support at scale"],
      revenue: "~$4M – $6M",
      milestone: "Multi-corridor scale + deeper support to importers & ultimate buyers",
      scale: "$100M – $150M",
    },
    {
      year: "Year 4",
      title: "Multi-Commodity Expansion + Buyer & Ultimate Buyer Support",
      focus: "Rice · Spices · Processed foods · Dairy · Pulses · Fruits & vegetables · Meat · Others",
      bullets: ["500+ ecosystem participants", "$250M – $400M trade flow", "Buyer support across commodities + ultimate buyers"],
      revenue: "Significant scale",
      milestone: "Multi-commodity scale + multi-sided support across the ecosystem",
      scale: "$250M – $400M",
    },
    {
      year: "Year 5",
      title: "Infrastructure Scale",
      focus: "Global food trade infrastructure",
      bullets: ["1,000+ exporters", "300+ buyers / importers / ultimate buyers", "$1B+ platform trade flow"],
      revenue: "~$40M – $60M",
      milestone: "Become the operating infrastructure of global food trade",
      scale: "$750M – $1.2B",
    },
  ];

  const drivers = [
    { icon: Network,    title: "Network Effects",   desc: "More participants → more transactions → stronger network" },
    { icon: Database,   title: "Data Advantage",    desc: "Proprietary trade & supply intelligence layer" },
    { icon: Compass,    title: "Corridor Expansion",desc: "Same infrastructure, more geographies" },
    { icon: Layers,     title: "Commodity Expansion",desc: "Same engine, more categories" },
    { icon: Zap,        title: "Capital Efficiency",desc: "Faster cycles, higher velocity, better outcomes" },
  ];

  const revenueMix = [
    { label: "Execution & Transaction Fees",         pct: 35 },
    { label: "Working Capital Services",             pct: 30 },
    { label: "Importer & Buyer Support Services",    pct: 20 },
    { label: "Membership & Platform Subscriptions",  pct: 10 },
    { label: "Data & Commercial Intelligence",       pct: 5  },
  ];

  return (
    <section className="bg-[#050A07] py-20 sm:py-24" data-testid="pitch-slide-five-year">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div {...fade(0)} className="mb-12">
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold mb-3">5-Year Plan · Market Scale Opportunity</p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
            From corridor proof to global infrastructure
          </h2>
          <p className="text-[#A1B2A8] text-base sm:text-lg mt-4 max-w-3xl">
            We start by solving working-capital friction in one corridor. We scale by becoming the operating infrastructure of global food trade.
          </p>
        </motion.div>

        {/* Year cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-12">
          {years.map((y, i) => (
            <motion.div key={y.year} {...fade(0.05 + i * 0.05)} className="border border-[#1A2E22] bg-[#09120D] p-5 relative flex flex-col" data-testid={`year-card-${i + 1}`}>
              <span className="absolute -top-2 left-4 bg-[#22C55E] text-[#050A07] text-[9px] font-bold uppercase tracking-wider px-2 py-0.5">{y.year}</span>
              <p className="text-white font-['Outfit'] font-bold text-base mt-3 leading-tight">{y.title}</p>
              <p className="text-[#22C55E]/80 text-[11px] mt-1 leading-snug">{y.focus}</p>
              <ul className="text-[#A1B2A8]/80 text-[11px] mt-3 space-y-1 leading-relaxed flex-1">
                {y.bullets.map((b, bi) => <li key={bi}>· {b}</li>)}
              </ul>
              <div className="mt-4 pt-3 border-t border-[#1A2E22] space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[#A1B2A8]/60 text-[9px] uppercase tracking-wider">Revenue</span>
                  <span className="text-white text-[11px] font-semibold">{y.revenue}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A1B2A8]/60 text-[9px] uppercase tracking-wider">Indicative Scale</span>
                  <span className="text-[#22C55E] text-[11px] font-bold">{y.scale}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Revenue mix */}
        <motion.div {...fade(0.35)} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <div className="border border-[#1A2E22] bg-[#09120D] p-6">
            <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">5-Year Revenue Mix (Indicative)</p>
            <div className="space-y-3">
              {revenueMix.map((r) => (
                <div key={r.label}>
                  <div className="flex justify-between mb-1.5">
                    <span className="text-white text-xs">{r.label}</span>
                    <span className="text-[#22C55E] text-xs font-bold">{r.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[#050A07]"><div className="h-full bg-[#22C55E]" style={{ width: `${r.pct * 2.5}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[#1A2E22] bg-[#09120D] p-6">
            <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">Market Size Anchors</p>
            <div className="space-y-3">
              {[
                { l: "TAM",          v: "$8T+",        s: "Global Food Commerce Ecosystem", c: "text-[#22C55E]" },
                { l: "SAM",          v: "$300B–$500B", s: "Cross-border food corridors",    c: "text-cyan-400" },
                { l: "SOM (3 yrs)",  v: "$1B–$3B",     s: "Obtainable across key corridors", c: "text-purple-400" },
              ].map((m) => (
                <div key={m.l} className="border border-[#1A2E22] bg-[#050A07] p-4 flex items-baseline justify-between">
                  <div>
                    <p className={`${m.c} text-[10px] uppercase tracking-wider font-bold`}>{m.l}</p>
                    <p className="text-[#A1B2A8]/70 text-[10px] mt-1">{m.s}</p>
                  </div>
                  <p className="text-white font-['Outfit'] font-bold text-lg">{m.v}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* What drives outcome */}
        <motion.div {...fade(0.4)}>
          <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">What drives our outcome</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {drivers.map((d) => (
              <div key={d.title} className="border border-[#1A2E22] bg-[#09120D] p-4">
                <d.icon className="w-4 h-4 text-[#22C55E] mb-2" />
                <p className="text-white text-sm font-semibold">{d.title}</p>
                <p className="text-[#A1B2A8]/70 text-[11px] mt-1 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <p className="text-[#A1B2A8]/40 text-[10px] mt-8 italic">Note: Numbers are approximate and projected. Sources: FAO 2024 · World Bank 2024 · WTO 2024 · Statista 2024.</p>
      </div>
    </section>
  );
}

/* =========================== EXPORT (composed deck) =========================== */
export function InvestorPitchDeck() {
  return (
    <div data-testid="investor-pitch-deck">
      <SlideBigPicture />
      <SlidePilot />
      <SlideMarketSize />
      <SlideFiveYearPlan />
    </div>
  );
}
